import React, { useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import uniqueId from 'lodash/uniqueId';

import ReadMore from 'components/ReadMore';
import PageLayout from 'layouts/PageLayout';
import ProfileHeader from 'components/ProfileHeader';
import Sidebar from 'scenes/Sidebar';
import CandidateProfile from 'scenes/CandidateProfile';
import PanelContainer from 'components/PanelContainer';
import ProfileContentLoader from 'components/ProfileContentLoader';
import BasicSection from 'components/BasicSection';
import ProfileItemEducation from 'scenes/CandidateProfile/components/ProfileItemEducation';
import ProfileItemPosition from 'scenes/CandidateProfile/components/ProfileItemPosition';
import ProfileItemReference from 'scenes/CandidateProfile/components/ProfileItemReference';
import ProfileViewed from 'scenes/Sidebar/components/ProfileViewed';
import Connections from 'scenes/Sidebar/components/Connections';
import DataList from 'components/DataList';
import ProfileItemTestResults from 'scenes/CandidateProfile/components/ProfileItemTestResults';
import CandidateInterviewDataWidget from 'scenes/Sidebar/components/CandidateInterviewDataWidget';

import { Permission, Role, Option } from 'helpers/user';
import { POSITION_FLAG } from 'constants/profile';
import { ENDPOINT } from 'constants/endpoints';
import { ROUTE } from 'constants/route';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation.js';
import { useApi } from 'hooks/useApi';

import action from './actions';
import './styles.css';

const CandidateProfileOwn = props => {
  const {
    user,
    history,
    location,
    globalActions: { updateUserPicture, updateUserResume },
  } = props;
  const api = useApi();

  const [windowWidth, setWindowWidth] = useState();

  const [state, setState] = useState({
    init: false,
    profile: {},
  });

  const loadData = () => {
    api.get(
      ENDPOINT.ENTITY.PROFILE,
      {},
      {
        handleForbidden: () => history.replace(ROUTE.ERROR_404),
        handleNotFound: () => history.replace(ROUTE.ERROR_404),
        handleSuccess: profile =>
          setState({
            init: true,
            profile: { ...profile },
          }),
      }
    );
  };

  useEffect(() => {
    setWindowWidth(window.innerWidth + 'px');
    loadData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get('action');

    if (state.init && action === 'updateResume') {
      history.replace(location.pathname);
      handleUploadResume();
    }

    if (state.init && action === 'updateInterviewData') {
      history.replace(location.pathname);
      handleInterviewDataUpdate();
    }
  }, [state]);

  const [isRecruiter] = useMemo(() => [Role.isRecruiter(user)], [user]);

  const handleEditPhoto = () =>
    action.editPhoto(state, { setState, windowWidth, updateUserPicture });

  const handleDeletePhoto = () =>
    action.deletePhoto(state, api, {
      setState,
      updateUserPicture,
    });

  const handleUploadResume = () =>
    action.uploadResume(state, {
      setState,
      updateUserResume,
      hasResume: Option.hasResume(user.options),
    });

  const handleAddPosition = () =>
    action.addPosition(state, isRecruiter, { loadData });

  const handleEditPosition = ({ PositionID }) =>
    action.editPosition(state, { PositionID }, { setState });

  const handleAddEducation = () =>
    action.addEducation(isRecruiter, { loadData });

  const handleEditEducation = ({ PositionID }) =>
    action.editEducation(state, { PositionID }, { setState });

  const handleAddReference = () => action.addReference(state, { setState });

  const handleEditAcademics = () =>
    action.editConfidentialInfo(state, 'Academics', { setState });

  const handleEditContacts = () =>
    action.editConfidentialInfo(state, 'Contact', { setState });

  const handleEditPersonalInfo = () =>
    action.editPersonalInfo(state, { setState });

  const handleEditPreferences = () =>
    action.editPreferences(state, { setState });

  const handleAddRecommendation = ({ PositionID }) =>
    action.addRecommendation({ PositionID });

  const handleEditSummary = () => action.editSummary(state, { setState });

  const handleBeforeSetFirmRating = () => action.beforeSetFirmRating(api, user);

  const handleSetFirmRating = ({ FirmID }, mark) =>
    action.setFirmRating(state, api, user, { id: FirmID, mark }, { setState });

  const handleInterviewDataUpdate = () =>
    action.editInterviewData(state, { setState });

  /* Render Blocks  */

  const renderHeader = useMemo(() => {
    if (state.init) {
      const {
        FullName,
        PictureUrl,
        EmailAddress,
        ResumeUpdateData,
        ResumeUrl,
        Confidential: { EmailAddress2, PhoneNumber1, PhoneNumber2 },
        Positions: [position],
      } = state.profile;

      return (
        <ProfileHeader
          data={{
            FullName,
            PictureUrl,
            EmailAddress,
            ResumeUpdateData,
            ResumeUrl,
            EmailAddress2,
            PhoneNumber1,
            PhoneNumber2,
            position,
          }}
          actions={{
            onPhotoEditHandler: handleEditPhoto,
            onPhotoDeleteHandler: handleDeletePhoto,
            onResumeUploadHandler: handleUploadResume,
            onEditConfidential: handleEditContacts,
            onAddRecommendation: handleAddRecommendation,
            onEditPosition: handleEditPosition,
            onEditEducation: handleEditEducation,
            onSetFirmRating: handleSetFirmRating,
            onBeforeSetFirmRating: handleBeforeSetFirmRating,
            onDownloadResume: () => action.downloadResume(ResumeUrl),
          }}
        />
      );
    }

    return <ProfileContentLoader />;
  }, [state.profile]);

  const renderExperience = useMemo(() => {
    if (state.init) {
      const { Positions } = state.profile;
      const experience =
        Positions && Positions.length > 0
          ? Positions.filter(
              (position) =>
                position.flag === POSITION_FLAG.POSITION
            )
          : null;

      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection
            title="Experience"
            buttons={[
              {
                caption: 'Add Position',
                handler: handleAddPosition,
              },
            ]}
          >
            {experience &&
              experience.length > 0 &&
              experience.map(position => (
                <ProfileItemPosition
                  data={position}
                  actions={{
                    onAddRecommendation: handleAddRecommendation,
                    onEditPosition: handleEditPosition,
                    onSetFirmRating: handleSetFirmRating,
                    onBeforeSetFirmRating: handleBeforeSetFirmRating,
                  }}
                  key={position.PositionID}
                />
              ))}
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile]);

  const renderEducation = useMemo(() => {
    if (state.init) {
      const { Positions } = state.profile;

      const education =
        Positions && Positions.length > 0
          ? Positions.filter(
              (position, index) =>
                index !== 0 &&
                position.flag === POSITION_FLAG.EDUCATION &&
                Number(position.Present) !== 1
            )
          : null;

      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection
            title="Education"
            buttons={[
              {
                caption: 'Add Education',
                handler: handleAddEducation,
              },
            ]}
          >
            {education &&
              education.length > 0 &&
              education.map(position => (
                <ProfileItemEducation
                  data={position}
                  actions={{
                    onAddRecommendation: handleAddRecommendation,
                    onEditEducation: handleEditEducation,
                  }}
                  key={position.PositionID}
                />
              ))}
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile]);

  const renderAcademics = useMemo(() => {
    if (state.init) {
      const {
        Confidential: {
          UndergraduateMajor,
          GmatName,
          GpaName,
          MathSatName,
          ScoreName,
          VerbalSatName,
          WritingSatName,
        },
      } = state.profile;

      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection
            title="Academics"
            controls={[
              {
                caption: 'Edit',
                handler: handleEditAcademics,
              },
            ]}
          >
            <div
              className="candidate-profile-own-page__text"
              style={{ margin: '15px 0' }}
            >
              <DataList
                objectList={{
                  'Undergraduate Major': UndergraduateMajor,
                  GPA: GpaName,
                  'SAT (M/V/W)': `${MathSatName}/${VerbalSatName}/${WritingSatName}`,
                  'ACT Score': ScoreName,
                  GMAT: GmatName,
                }}
              />
            </div>
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile]);

  const renderSummary = useMemo(() => {
    if (state.init) {
      const { Summary } = state.profile;
      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection
            title="Summary"
            controls={[
              {
                caption: 'Edit',
                handler: handleEditSummary,
              },
            ]}
          >
            <div
              className="candidate-profile-own-page__text"
              style={{ margin: '15px 0' }}
            >
              {Summary && (
                <ReadMore text={Summary} readMoreCharacterLimit={350} />
              )}
            </div>
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile]);

  const renderPreferences = useMemo(() => {
    if (state.init) {
      const {
        Preferences: { buyside, location, fundsize },
      } = state.profile;

      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection
            title="Preferences"
            controls={[
              {
                caption: 'Edit',
                handler: handleEditPreferences,
              },
            ]}
          >
            <DataList
              objectList={{
                'Investment Strategy Interests': buyside,
                Location: location,
                'Fund Size': fundsize,
              }}
            />
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile]);

  const renderPersonalInformation = useMemo(() => {
    if (state.init) {
      const {
        profile: {
          ManagePersonalAccount,
          ImmediateStartOpportunities,
          Languages,
          Citizenship,
        },
      } = state;

      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection
            title="Personal Information"
            controls={[
              {
                caption: 'Edit',
                handler: handleEditPersonalInfo,
              },
            ]}
          >
            <DataList
              objectList={{
                'Manage Account': ManagePersonalAccount,
                'Immediate Start': ImmediateStartOpportunities,
                'Foreign Languages': Object.values(Languages).map(
                  language => `${language.LanguageName} (${language.LevelName})`
                ),
                Citizenship: Citizenship,
              }}
            />
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile]);

  const renderReferences = useMemo(() => {
    if (state.init) {
      const { References } = state.profile;
      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection
            title="References"
            buttons={[
              {
                caption: 'Add Reference',
                handler: handleAddReference,
              },
            ]}
          >
            {References && References.length > 0 && (
              <div className="candidate-profile-own-page__reference-list">
                {References.map(record => (
                  <ProfileItemReference
                    data={record}
                    action={{}}
                    key={uniqueId()}
                  />
                ))}
              </div>
            )}
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile]);

  const renderAptitudeTests = useMemo(() => {
    if (state.init) {
      const { AptitudeTests } = state.profile;
      const aptitudeTests = AptitudeTests ? Object.values(AptitudeTests) : null;

      return (
        aptitudeTests &&
        aptitudeTests.length > 0 && (
          <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
            <BasicSection title="GoBuyside Aptitude Test">
              <div className="candidate-profile-own-page__test-results-list">
                {aptitudeTests.map(item => (
                  <ProfileItemTestResults
                    data={item}
                    showPercentile={Permission.isAllowedViewGATPercentile(props.user.permissions)}
                    user={props.user}
                    history={props.history}
                    key={uniqueId()}
                  />
                ))}
              </div>
            </BasicSection>
          </PanelContainer>
        )
      );
    }
  }, [state.profile]);

  return (
    <PageLayout
      pageClass="candidate-profile-own-page"
      pageTitle="Profile"
      headerContent={renderHeader}
      mainContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <CandidateProfile>
            {renderExperience}
            {renderSummary}
            {renderEducation}
            {renderAcademics}
            {renderPreferences}
            {renderPersonalInformation}
            {renderReferences}
            {renderAptitudeTests}
          </CandidateProfile>
        </CSSTransition>
      }
      sidebarContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <Sidebar>
            <CandidateInterviewDataWidget
              data={state.profile.InterviewData}
              onClickButton={handleInterviewDataUpdate}
            />
            <ProfileViewed isOwnProfile history={history} user={user} />
            <Connections user={user} />
          </Sidebar>
        </CSSTransition>
      }
    />
  );
};

export default CandidateProfileOwn;
